export interface SurveyExportResponse {
  ok: boolean;
  status: number;
  headers: Pick<Headers, 'get'>;
  blob: () => Promise<Blob>;
}

export type SurveyExportFetch = (
  input: RequestInfo,
  init?: RequestInit
) => Promise<SurveyExportResponse>;

export interface SurveyExportDocument {
  createElement: (tagName: 'a') => HTMLAnchorElement;
  body: Pick<HTMLElement, 'appendChild' | 'removeChild'>;
}

export interface SurveyExportUrlApi {
  createObjectURL: (blob: Blob) => string;
  revokeObjectURL: (url: string) => void;
}

export interface DownloadSurveyExportOptions {
  url: string;
  fallbackFilename: string;
  fetchImpl?: SurveyExportFetch;
  documentRef?: SurveyExportDocument;
  urlApi?: SurveyExportUrlApi;
}

const decodeFilename = (value: string, plusAsSpace = false): string => {
  const filename = value.trim().replace(/^"|"$/g, '');
  const encodedFilename = plusAsSpace ? filename.replace(/\+/g, ' ') : filename;
  try {
    return decodeURIComponent(encodedFilename);
  } catch (error) {
    return encodedFilename;
  }
};

const isExpectedWorkbookContentType = (contentType: string | null): boolean => {
  if (!contentType) {
    return false;
  }
  return /^(?:application\/(?:octet-stream|vnd\.ms-excel|x-msexcel|xls)|binary\/octet-stream)(?:;|$)/i.test(
    contentType.trim()
  );
};

export const getDownloadFilename = (
  contentDisposition: string | null,
  fallbackFilename: string
): string => {
  if (!contentDisposition) {
    return fallbackFilename;
  }

  const encodedFilename = contentDisposition.match(
    /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i
  );
  if (encodedFilename?.[1]) {
    return decodeFilename(encodedFilename[1]);
  }

  const quotedFilename = contentDisposition.match(
    /filename\s*=\s*"([^"]+)"/i
  );
  if (quotedFilename?.[1]) {
    return quotedFilename[1];
  }

  const plainFilename = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
  return plainFilename?.[1]
    ? decodeFilename(plainFilename[1], true)
    : fallbackFilename;
};

export const downloadSurveyExport = async ({
  url,
  fallbackFilename,
  fetchImpl = window.fetch.bind(window) as SurveyExportFetch,
  documentRef = document,
  urlApi = URL,
}: DownloadSurveyExportOptions): Promise<void> => {
  const response = await fetchImpl(url, { credentials: 'same-origin' });
  if (!response.ok || response.status !== 200) {
    throw new Error(`Unexpected survey export response: HTTP ${response.status}`);
  }

  const contentType = response.headers.get('content-type');
  if (!isExpectedWorkbookContentType(contentType)) {
    throw new Error(`Unexpected survey export content type: ${contentType}`);
  }

  const blob = await response.blob();
  if (blob.size === 0) {
    throw new Error('Survey export returned an empty workbook');
  }

  const objectUrl = urlApi.createObjectURL(blob);
  let anchor: HTMLAnchorElement | undefined;
  let appended = false;
  try {
    anchor = documentRef.createElement('a');
    anchor.href = objectUrl;
    anchor.download = getDownloadFilename(
      response.headers.get('content-disposition'),
      fallbackFilename
    );
    anchor.style.display = 'none';
    documentRef.body.appendChild(anchor);
    appended = true;
    anchor.click();
  } finally {
    try {
      if (appended && anchor) {
        documentRef.body.removeChild(anchor);
      }
    } finally {
      urlApi.revokeObjectURL(objectUrl);
    }
  }
};
